import React from "react";
import { View, Text, Platform } from "react-native";
import commonStyle from "../../assets/styles/common-style";
import {
  dateFormatter,
  displayPhoneFormatter,
} from "@lib/utilities/Formatters";
import { DocsInfoConstants } from "@lib/utilities/Constants";
import ListHeading from "./ListHeading";
import { createPlatformStyles, theme } from "../../theme/theme";

interface PolicyHolderSectionProps {
  policyHolders: any[];
  mailingAddress?: string;
  effectiveDate?: string;
  endDate?: string;
}

const PolicyHolderSection: React.FC<PolicyHolderSectionProps> = ({
  policyHolders,
  mailingAddress,
  effectiveDate,
  endDate,
}) => {
  return (
    <>
      <ListHeading title={`${DocsInfoConstants.policyDetails}`} page={"docs"} />
      <View
        style={[styles.policyHolderContainer]}
        dataSet={{ media: ids.policyHolderContainer }}
        testID={`docs-policy-details-content-container`}
      >
        <View
          style={[commonStyle.styles.rowFlex, styles.policyHolderInfo]}
          dataSet={{ media: ids.policyHolderInfo }}
        >
          <PolicyInfoField
            label={DocsInfoConstants.policyEffectiveDate}
            value={`${dateFormatter(effectiveDate)}`}
          />
          <PolicyInfoField
            label={DocsInfoConstants.policyExpirationDate}
            value={`${dateFormatter(endDate)}`}
          />
        </View>
        {policyHolders.map((policy: any, index: number) => (
          <View key={index + policy._id}>
            {index === 0 && mailingAddress && (
              <View
                style={[commonStyle.styles.rowFlex, styles.policyHolderInfo]}
                dataSet={{ media: ids.policyHolderInfo }}
              >
                <PolicyInfoField
                  label={DocsInfoConstants.mailingAddress}
                  value={`${mailingAddress}`}
                />
              </View>
            )}
            <View
              style={[commonStyle.styles.colFlex, { marginTop: theme.margin.mmd }]}
              dataSet={{ media: commonStyle.ids.colFlex }}
            >
              <View
                style={[
                  commonStyle.styles.rowFlexAlignCenter
                ]}
              >
                <View>
                  <PolicyInfoTitle
                    title={`${DocsInfoConstants.policyholder} ${index + 1}`}
                  />
                </View>
                <View style={{ flex: 1, padding: 12 }}>
                  <View style={styles.divider} />
                </View>
              </View>
              <View
                style={[commonStyle.styles.rowFlex, styles.policyHolderInfo]}
                dataSet={{ media: ids.policyHolderInfo }}
              >
                <PolicyInfoField
                  label={DocsInfoConstants.policyholderName}
                  value={`${policy.firstName} ${policy.lastName}`}
                />
                <PolicyInfoField
                  label={DocsInfoConstants.phone}
                  value={displayPhoneFormatter(policy.primaryPhoneNumber)}
                />
              </View>
              <PolicyInfoField
                label={DocsInfoConstants.email}
                value={policy.emailAddress}
              />
            </View>
          </View>
        ))}
      </View>
    </>
  );
};

interface PolicyInfoFieldProps {
  label: string;
  value: string;
  addPadding?: boolean;
  containerStyle?: any;
  containerMedia?: any;
}
interface PolicyInfoTitleTypes {
  title: string;
}

const PolicyInfoField: React.FC<PolicyInfoFieldProps> = ({
  label,
  value,
  addPadding,
  containerStyle,
  containerMedia,
}) => (
  <View
    style={[commonStyle.styles.colFlex, containerStyle || styles.twoColLayout]}
    dataSet={{ media: containerMedia || commonStyle.ids.colFlex }}
    testID="docs-page-policy-detail-section-row"
  >
    <Text
      style={[styles.textLabelPolicy]}
      dataSet={{ media: ids.textLabelPolicy }}
    >
      {label}
    </Text>
    <Text
      style={[styles.descText, addPadding && styles.paddingRight]}
      dataSet={{ media: ids.descText }}
    >
      {value}
    </Text>
  </View>
);

const PolicyInfoTitle: React.FC<PolicyInfoTitleTypes> = ({ title }) => (
  <Text
    style={[styles.textLabelPolicyTitle]}
    dataSet={{ media: ids.textLabelPolicyTitle }}
  >
    {title}
  </Text>
);

const { styles, ids } = createPlatformStyles({
  mobile: {
    paddingRight: {
      paddingRight: theme.spacing.xlg,
    },
    policyHolderContainer: {
      marginBottom: 12,
      backgroundColor: theme.colors.white,
      paddingBottom: 12,
      borderBottomEndRadius: 10,
      borderBottomLeftRadius: 10,
      position: "relative",
      zIndex: 8,
    },
    divider: {
      width: "100%",
      height: 1,
      borderRadius: 50,
      backgroundColor: theme.colors.black + 35,
    },
    colOne: {
      ...Platform.select({
        ios: {
          "@media (min-width: 200px)": {
            paddingRight: 0,
            display: "flex",
            flexDirection: "column",
          },
          "@media (min-width: 600px)": {
            display: "flex",
            flexDirection: "row",
          },
        },
        android: {
          "@media (min-width: 200px)": {
            paddingRight: 0,
            display: "flex",
            flexDirection: "column",
          },
          "@media (min-width: 600px)": {
            display: "flex",
            flexDirection: "row",
          },
        },
        web: {
          flex: 1,
          "@media (min-width: 200px)": {
            display: "flex",
            flexDirection: "column",
            paddingRight: 12,
          },
          "@media (min-width: 600px)": {
            display: "flex",
            flexDirection: "row",
            paddingRight: 0,
          },
        },
      }),
    },
    policyHolderInfo: {
      "@media (min-width: 200px)": {
        flexDirection: "column",
        marginBottom: 8,
      },
      "@media (min-width: 700px)": {
        flexDirection: "row",
      },
    },
    twoColLayout: {
      ...Platform.select({
        ios: {
          "@media (min-width: 200px)": {
            minWidth: "100%",
            maxWidth: "100%",
            marginTop: 12,
          },
          "@media (min-width: 700px)": {
            minWidth: "50%",
            maxWidth: "50%",
            marginTop: 12,
          },
        },
        android: {
          "@media (min-width: 200px)": {
            minWidth: "100%",
            maxWidth: "100%",
            marginTop: 12,
          },
          "@media (min-width: 700px)": {
            minWidth: "50%",
            maxWidth: "50%",
            marginTop: 12,
          },
        },
        web: {
          minWidth: "50%",
          maxWidth: "50%",
          marginTop: 16,
          "@media (min-width: 200px)": {
            minWidth: "100%",
            maxWidth: "100%",
            marginTop: 12,
          },
          "@media (min-width: 700px)": {
            minWidth: "50%",
            maxWidth: "50%",
            marginTop: 16,
          },
        },
      }),
    },
    twoColLayoutSingleView: {
      ...Platform.select({
        ios: {
          "@media (min-width: 200px)": {
            minWidth: "100%",
            maxWidth: "100%",
            marginTop: 12,
          },
          "@media (min-width: 700px)": {
            minWidth: "100%",
            maxWidth: "100%",
            marginTop: 12,
          },
        },
        android: {
          "@media (min-width: 200px)": {
            minWidth: "100%",
            maxWidth: "100%",
            marginTop: 12,
          },
          "@media (min-width: 700px)": {
            minWidth: "100%",
            maxWidth: "100%",
            marginTop: 12,
          },
        },
        web: {
          minWidth: "100%",
          maxWidth: "100%",
          marginTop: 16,
          "@media (min-width: 200px)": {
            minWidth: "100%",
            maxWidth: "100%",
            marginTop: 12,
          },
          "@media (min-width: 700px)": {
            minWidth: "100%",
            maxWidth: "100%",
            marginTop: 16,
          },
        },
      }),
    },
    textLabelPolicy: {
      fontFamily: theme.fontFamily.robotoRegular,
      fontSize: theme.fontsize.reg,
      color: theme.colors.text.secondary,
      paddingLeft: theme.spacing.mmd,
      marginBottom: theme.fontsize.xxs,
    },
    textLabelPolicyTitle: {
      fontFamily: theme.fontFamily.robotoRegular,
      fontSize: theme.fontsize.cs,
      color: theme.colors.text.secondary,
      paddingLeft: theme.spacing.mmd,
      fontWeight:theme.fontweight.regular,
    },
    descText: {
      fontFamily: theme.fontFamily.robotoRegular,
      fontSize: theme.fontsize.cs,
      paddingLeft: theme.spacing.mmd,
      color: theme.colors.text.primary,
    },
  },
  web: {
    divider: {
      width: "100%",
      height: 1,
      borderRadius: 50,
      backgroundColor: theme.colors.black + 35,
    },
    paddingRight: {
      paddingRight: theme.spacing.xlg,
    },
    policyHolderContainer: {
      marginBottom: theme.spacing.reg,
      backgroundColor: theme.colors.white,
      paddingBottom: theme.spacing.reg,
      borderBottomEndRadius: theme.radius.sm,
      borderBottomLeftRadius: theme.radius.sm,
      position: "relative",
      zIndex: 8,
    },
    colOne: {
      "@media (min-width: 200px)": {
        paddingRight: 0,
        display: "flex",
        flexDirection: "column",
      },
      "@media (min-width: 600px)": {
        display: "flex",
        flexDirection: "row",
      },
      ...Platform.select({
        web: {
          flex: 1,
          "@media (min-width: 200px)": {
            display: "flex",
            flexDirection: "column",
            paddingRight: 12,
          },
          "@media (min-width: 600px)": {
            display: "flex",
            flexDirection: "row",
            paddingRight: 0,
          },
        },
      }),
    },
    policyHolderInfo: {
      "@media (min-width: 200px)": {
        flexDirection: "column",
        marginBottom: theme.margin.sm,
      },
      "@media (min-width: 700px)": {
        flexDirection: "row",
      },
    },
    twoColLayout: {
      "@media (min-width: 200px)": {
        minWidth: "100%",
        maxWidth: "100%",
        marginTop: theme.margin.reg,
      },
      "@media (min-width: 700px)": {
        minWidth: "50%",
        maxWidth: "50%",
        marginTop: theme.margin.reg,
      },
      ...Platform.select({
        web: {
          minWidth: "50%",
          maxWidth: "50%",
          marginTop: theme.margin.md,
          "@media (min-width: 200px)": {
            minWidth: "100%",
            maxWidth: "100%",
            marginTop: theme.margin.reg,
          },
          "@media (min-width: 700px)": {
            minWidth: "50%",
            maxWidth: "50%",
            marginTop: theme.margin.md,
          },
        },
      }),
    },
    twoColLayoutSingleView: {
      ...Platform.select({
        ios: {
          "@media (min-width: 200px)": {
            minWidth: "100%",
            maxWidth: "100%",
            marginTop: theme.margin.reg,
          },
          "@media (min-width: 700px)": {
            minWidth: "100%",
            maxWidth: "100%",
            marginTop: theme.margin.reg,
          },
        },
        android: {
          "@media (min-width: 200px)": {
            minWidth: "100%",
            maxWidth: "100%",
            marginTop: theme.margin.reg,
          },
          "@media (min-width: 700px)": {
            minWidth: "100%",
            maxWidth: "100%",
            marginTop: theme.margin.reg,
          },
        },
        web: {
          minWidth: "100%",
          maxWidth: "100%",
          marginTop: 16,
          "@media (min-width: 200px)": {
            minWidth: "100%",
            maxWidth: "100%",
            marginTop: theme.margin.reg,
          },
          "@media (min-width: 700px)": {
            minWidth: "100%",
            maxWidth: "100%",
            marginTop: 16,
          },
        },
      }),
    },
    textLabelPolicy: {
      fontFamily: theme.fontFamily.robotoRegular,
      fontSize: theme.fontsize.reg,
      color: theme.colors.text.secondary,
      paddingLeft: theme.spacing.mmd,
      marginBottom: theme.fontsize.xxs,
      fontWeight: theme.fontweight.light,
    },
    textLabelPolicyTitle: {
      fontFamily: theme.fontFamily.robotoRegular,
      fontSize: theme.fontsize.cs,
      color: theme.colors.text.secondary,
      paddingLeft: theme.spacing.mmd,
      fontWeight:theme.fontweight.regular,
    },
    descText: {
      fontFamily: theme.fontFamily.robotoRegular,
      fontSize: theme.fontsize.cs,
      paddingLeft: theme.spacing.mmd,
      color: theme.colors.text.primary,
      fontWeight: theme.fontweight.light,
    },
  },
});
export default PolicyHolderSection;
